// Shared formatting for the kitchen scheduling screens. Times always read in
// bakery local time — the server sends the local HH:MM alongside every UTC
// instant, so nothing here re-derives a timezone.

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const fmtTime = (hhmm) => {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
};

export const fmtWindow = (start, end) => `${fmtTime(start)} – ${fmtTime(end)}`;

// Parse a date-only string without letting UTC shift the day.
const asDate = (dateStr) => new Date(`${dateStr}T12:00:00`);

export const fmtDate = (dateStr) =>
  dateStr
    ? asDate(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '—';

export const fmtDateLong = (dateStr) =>
  dateStr
    ? asDate(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '—';

export const fmtDayNumber = (dateStr) => (dateStr ? asDate(dateStr).getDate() : '');

export const addDays = (dateStr, days) => {
  const d = asDate(dateStr);
  d.setDate(d.getDate() + days);
  return toDateOnly(d);
};

export const toDateOnly = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${day}`;
};

export const todayStr = () => toDateOnly(new Date());

// 0 = Monday .. 6 = Sunday, matching the backend.
export const isoWeekday = (dateStr) => (asDate(dateStr).getDay() + 6) % 7;

export const weekStartOf = (dateStr, weekStartDay = 0) => {
  const delta = (isoWeekday(dateStr) - weekStartDay + 7) % 7;
  return addDays(dateStr, -delta);
};

export const money = (value) =>
  `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const hoursLabel = (value) => `${Number(value || 0).toFixed(Number(value) % 1 === 0 ? 0 : 1)}h`;

// Coverage levels carry colour AND text so colour is never the only signal.
export const LEVEL_STYLES = {
  red: {
    label: 'Not covered',
    card: 'border-l-4 border-l-red-500 bg-red-50/60',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
  },
  amber: {
    label: 'Needs attention',
    card: 'border-l-4 border-l-amber-500 bg-amber-50/60',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  green: {
    label: 'Covered',
    card: 'border-l-4 border-l-emerald-500 bg-white',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
};

export const levelStyle = (level) => LEVEL_STYLES[level] || LEVEL_STYLES.green;

export const POSITION_STYLES = {
  baker: 'bg-amber-100 text-amber-900 border-amber-300',
  fryer: 'bg-orange-100 text-orange-900 border-orange-300',
  finisher: 'bg-rose-100 text-rose-900 border-rose-300',
  packer: 'bg-violet-100 text-violet-900 border-violet-300',
  driver: 'bg-sky-100 text-sky-900 border-sky-300',
};

export const positionStyle = (code) => POSITION_STYLES[code] || 'bg-gray-100 text-gray-800 border-gray-300';
